import type { ReactNode } from 'react';

import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';

import { AlertSolidIcon } from '@/ui/Icons/AlertSolidIcon';
import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import { NotificationIndicatorIcon } from '@/ui/Icons/NotificationIndicatorIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

interface SectionHeadingProps {
  /** Optional alert component. */
  alert?: string;
  /** Indicator to display if the settings in the section are altered */
  isAltered?: boolean;
  /** The JSX content of the popover. */
  children: ReactNode;
  /** The title of the section. */
  title: string;
  /** Optional action component. */
  action?: ReactNode;
  /** className of styles to be applied to tooltip content */
  className?: string;
}

/**
 * Represents a heading for a trade setting section with a popover containing additional information.
 * Note: the popover trigger is a static info icon until the need arises to customize it.
 */
export const SectionHeading = ({
  alert,
  isAltered,
  children,
  title,
  action,
  className,
}: SectionHeadingProps) => {
  const { trigger: triggerStyles, triggerIcon: triggerIconStyles } = popover();

  const trigger = (
    <button className={triggerStyles()}>
      <InfoIconOutline className={triggerIconStyles()} />
    </button>
  );

  return (
    <h4 className="flex items-center justify-between mb-3">
      <div className={fontStyles({ variant: 'LABEL_MD', class: 'flex items-center' })}>
        {title}

        <MatchaTooltip delayDuration={0}>
          <MatchaTooltipTrigger>{trigger}</MatchaTooltipTrigger>
          <MatchaTooltipContent>{children}</MatchaTooltipContent>
          <MatchaTooltipPopover side="top" sideOffset={8} className={className} />
          <MatchaTooltipBottomSheet header={title} />
        </MatchaTooltip>

        {isAltered && <NotificationIndicatorIcon className="ml-2" />}

        {alert && (
          <span className="flex text-token-red-700 ml-2">
            <AlertSolidIcon className="mr-2 size-4" />
            {alert}
          </span>
        )}
      </div>
      {action}
    </h4>
  );
};
